import { FC, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useProfile } from '@/hooks';
import { useAuthContext } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { LoadingSpinner } from '@/components';
import ProfileEditModal from '@/components/ProfileEditModal';
import NotificationPreferencesSection from '@/components/NotificationPreferencesSection';
import InfoTooltip from '@/components/InfoTooltip';
import { TUTORIAL_KEYS } from '@/constants/tutorials';
import { profileService } from '@/services/profile.service';
import { feedService } from '@/services/feed.service';
import { certificateService } from '@/services/certificate.service';
import type { ProfileBadge } from '@/types/profile';
import type { Certificate } from '@/types/certificate';

const RARITY_STYLES: Record<string, { bg: string; text: string; border: string; label: string }> = {
  COMMON: { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/30', label: 'Comum' },
  RARE: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/30', label: 'Raro' },
  EPIC: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/30', label: 'Épico' },
  LEGENDARY: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30', label: 'Lendário' },
};

const BadgeCard: FC<{ badge: ProfileBadge; highlight?: boolean }> = ({ badge, highlight }) => {
  const style = RARITY_STYLES[badge.rarity] || RARITY_STYLES.COMMON;
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl border ${style.border} ${style.bg} ${highlight ? 'ring-1 ring-amber-500/40' : ''}`}>
      {badge.icon_url ? (
        <img src={badge.icon_url} alt={badge.title} className="size-10 rounded-lg object-cover" />
      ) : (
        <div className={`size-10 rounded-lg flex items-center justify-center ${style.bg}`}>
          <span className={`material-symbols-outlined ${style.text}`}>{badge.icon || 'emoji_events'}</span>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-content-primary truncate">{badge.title}</p>
        <p className={`text-xs font-medium ${style.text}`}>{style.label}</p>
      </div>
    </div>
  );
};

const ProfilePage: FC = () => {
  const { data, isLoading, error, refetch } = useProfile();
  const { updateProfile, refreshUser, resetTutorial } = useAuthContext();
  const { theme, toggleTheme } = useTheme();
  const [showEditModal, setShowEditModal] = useState(false);
  const [isTogglingPrivacy, setIsTogglingPrivacy] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [resettingKey, setResettingKey] = useState<string | null>(null);
  const [prefsToast, setPrefsToast] = useState<string | null>(null);
  const [certificates, setCertificates] = useState<Certificate[]>([]);

  useEffect(() => {
    feedService.getFollowersCount().then((count) => setFollowersCount(count)).catch(() => {});
    certificateService.list().then((certs) => setCertificates(certs)).catch(() => {});
  }, []);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-red-500">Erro ao carregar perfil: {error}</p>
      </div>
    );
  }

  const { user, ranking_position, season_xp, season_name, recent_badges, rarest_badge, total_badges, courses_in_progress, courses_completed, season_history } = data;

  const isProfilePublic = user.preferences?.privacy?.show_profile !== false;

  const handleTogglePrivacy = async () => {
    setIsTogglingPrivacy(true);
    try {
      await profileService.updateProfilePublic(!isProfilePublic);
      await refreshUser();
      await refetch();
    } catch {
      // silently fail
    } finally {
      setIsTogglingPrivacy(false);
    }
  };

  const handleResetTutorial = async (key: string, label: string) => {
    setResettingKey(key);
    try {
      await resetTutorial(key);
      setPrefsToast(`Tutorial "${label}" será exibido no próximo acesso.`);
    } catch {
      setPrefsToast(`Não foi possível reiniciar "${label}". Tente novamente.`);
    } finally {
      setResettingKey(null);
      setTimeout(() => setPrefsToast(null), 4000);
    }
  };

  const xpProgress = user.next_level_xp > 0
    ? ((user.next_level_xp - user.xp_to_next_level) / user.next_level_xp) * 100
    : 100;

  const memberSince = new Date(user.created_at).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });

  return (
    <div className="flex flex-1 justify-center py-8 bg-surface-primary">
      <div className="flex flex-col max-w-[900px] flex-1 px-4 md:px-10 gap-8">

        {/* Header */}
        <section className="flex flex-col sm:flex-row items-center sm:items-start gap-6 p-6 bg-surface-card border border-theme-border rounded-xl">
          <button
            type="button"
            onClick={() => setShowEditModal(true)}
            className="relative group shrink-0"
            aria-label="Editar perfil"
          >
            {user.avatar ? (
              <img src={user.avatar} alt={user.name} className="size-24 rounded-full object-cover border-2 border-theme-border" />
            ) : (
              <div className="size-24 rounded-full bg-accent/10 flex items-center justify-center border-2 border-theme-border">
                <span className="material-symbols-outlined text-4xl text-accent">person</span>
              </div>
            )}
            <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <span className="material-symbols-outlined text-white">photo_camera</span>
            </div>
          </button>
          <div className="flex-1 text-center sm:text-left">
            <h1 className="text-2xl font-bold text-content-primary">{user.name}</h1>
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-4 gap-y-1 mt-1 text-sm text-content-muted">
              {user.squad && (
                <span className="flex items-center gap-1">
                  <span className="material-symbols-outlined text-base">groups</span>
                  {user.squad.name}
                </span>
              )}
              {user.sector && (
                <span className="flex items-center gap-1">
                  <span className="material-symbols-outlined text-base">apartment</span>
                  {typeof user.sector === 'string' ? user.sector : user.sector.name}
                </span>
              )}
              <span className="flex items-center gap-1">
                <span className="material-symbols-outlined text-base">calendar_month</span>
                Membro desde {memberSince}
              </span>
            </div>
            {user.bio && <p className="text-sm text-content-secondary mt-2">{user.bio}</p>}
            {followersCount > 0 && (
              <p className="text-sm text-content-muted mt-2" data-testid="followers-count">
                {followersCount} {followersCount === 1 ? 'pessoa acompanha' : 'pessoas acompanham'} este perfil
              </p>
            )}
          </div>
          <button
            onClick={() => setShowEditModal(true)}
            className="shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg bg-accent/10 text-accent text-sm font-medium hover:bg-accent/20 transition-colors"
          >
            <span className="material-symbols-outlined text-base">edit</span>
            Editar Perfil
          </button>
        </section>

        {/* Privacy Toggle */}
        <section className="flex items-center justify-between p-4 bg-surface-card border border-theme-border rounded-xl" data-testid="privacy-toggle-section">
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined text-content-muted">
              {isProfilePublic ? 'visibility' : 'visibility_off'}
            </span>
            <div>
              <div className="flex items-center gap-1">
                <p className="text-sm font-medium text-content-primary">Perfil Público</p>
                <InfoTooltip termKey="perfil_publico" iconOnly />
              </div>
              <p className="text-xs text-content-muted">
                {isProfilePublic
                  ? 'Outros usuários podem ver seu perfil completo'
                  : 'Outros usuários veem apenas seu nome, avatar e nível'}
              </p>
            </div>
          </div>
          <button
            onClick={handleTogglePrivacy}
            disabled={isTogglingPrivacy}
            data-testid="privacy-toggle"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              isProfilePublic ? 'bg-accent' : 'bg-surface-tertiary'
            } ${isTogglingPrivacy ? 'opacity-50' : ''}`}
            role="switch"
            aria-checked={isProfilePublic}
          >
            <span
              className={`inline-block size-4 rounded-full bg-white transition-transform ${
                isProfilePublic ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </section>

        {/* Preferências */}
        <section className="p-6 bg-surface-card border border-theme-border rounded-xl">
          <h3 className="text-base font-bold text-content-primary mb-4">Preferências</h3>

          <div className="flex items-center justify-between gap-4 pb-4 mb-4 border-b border-theme-border">
            <div>
              <p className="text-sm font-medium text-content-primary">Tema da interface</p>
              <p className="text-xs text-content-muted">Escolha entre claro e escuro</p>
            </div>
            <button
              onClick={toggleTheme}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-surface-secondary hover:bg-surface-hover transition-colors text-sm font-medium text-content-primary"
              aria-label={theme === 'dark' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
            >
              <span className="material-symbols-outlined text-base">
                {theme === 'dark' ? 'light_mode' : 'dark_mode'}
              </span>
              {theme === 'dark' ? 'Tema Claro' : 'Tema Escuro'}
            </button>
          </div>

          <div>
            <h4 className="text-sm font-bold text-content-primary mb-3">Refazer tutoriais</h4>
            <div className="space-y-2">
              <button
                onClick={() => handleResetTutorial(TUTORIAL_KEYS.MAIN, 'Inicial')}
                disabled={resettingKey === TUTORIAL_KEYS.MAIN}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl bg-surface-secondary hover:bg-surface-hover transition-colors disabled:opacity-50 text-left"
              >
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-content-secondary">dashboard</span>
                  <div>
                    <p className="text-sm font-medium text-content-primary">Tutorial inicial</p>
                    <p className="text-xs text-content-muted">Tour de boas-vindas no Dashboard</p>
                  </div>
                </div>
                <span className="material-symbols-outlined text-content-muted text-base">restart_alt</span>
              </button>

              <button
                onClick={() => handleResetTutorial(TUTORIAL_KEYS.TRAILS, 'Cursos')}
                disabled={resettingKey === TUTORIAL_KEYS.TRAILS}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl bg-surface-secondary hover:bg-surface-hover transition-colors disabled:opacity-50 text-left"
              >
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-content-secondary">explore</span>
                  <div>
                    <p className="text-sm font-medium text-content-primary">Tutorial de Cursos</p>
                    <p className="text-xs text-content-muted">Como funcionam as Trilhas</p>
                  </div>
                </div>
                <span className="material-symbols-outlined text-content-muted text-base">restart_alt</span>
              </button>

              <button
                onClick={() => handleResetTutorial(TUTORIAL_KEYS.RANKING, 'Ranking')}
                disabled={resettingKey === TUTORIAL_KEYS.RANKING}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl bg-surface-secondary hover:bg-surface-hover transition-colors disabled:opacity-50 text-left"
              >
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-content-secondary">leaderboard</span>
                  <div>
                    <p className="text-sm font-medium text-content-primary">Tutorial de Ranking</p>
                    <p className="text-xs text-content-muted">Como ler as posições</p>
                  </div>
                </div>
                <span className="material-symbols-outlined text-content-muted text-base">restart_alt</span>
              </button>
            </div>
          </div>
        </section>

        {/* Notification Preferences */}
        <NotificationPreferencesSection />

        {/* Level */}
        <section className="p-6 bg-surface-card border border-theme-border rounded-xl">
          <div className="flex items-center gap-4 mb-4">
            {user.level_icon_url ? (
              <img src={user.level_icon_url} alt={user.level_name} className="size-12 rounded-lg object-cover" />
            ) : (
              <div className="size-12 rounded-lg bg-accent/10 flex items-center justify-center">
                <span className="material-symbols-outlined text-2xl text-accent">military_tech</span>
              </div>
            )}
            <div>
              <p data-testid="user-level" className="text-lg font-bold text-content-primary">Nível {user.level} — {user.level_name}</p>
              <p className="text-sm text-content-muted">
                {user.xp_to_next_level > 0
                  ? `${user.xp_to_next_level.toLocaleString('pt-BR')} XP para o próximo nível`
                  : 'Nível máximo atingido!'}
              </p>
            </div>
          </div>
          <div className="w-full h-3 bg-surface-tertiary rounded-full overflow-hidden">
            <div
              className="h-full bg-accent rounded-full transition-all duration-500"
              style={{ width: `${Math.min(xpProgress, 100)}%` }}
            />
          </div>
          <div className="flex justify-between mt-2 text-xs text-content-muted">
            <span>{(user.next_level_xp - user.xp_to_next_level).toLocaleString('pt-BR')} XP</span>
            <span>{user.next_level_xp > 0 ? user.next_level_xp.toLocaleString('pt-BR') : ''} XP</span>
          </div>
        </section>

        {/* Stats Grid */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex flex-col items-center p-4 bg-surface-card border border-theme-border rounded-xl">
            <span className="material-symbols-outlined text-2xl text-accent mb-1">stars</span>
            <span data-testid="xp-total" className="text-xl font-bold text-content-primary">{user.total_xp.toLocaleString('pt-BR')}</span>
            <span className="text-xs text-content-muted">XP Total</span>
          </div>
          <div className="flex flex-col items-center p-4 bg-surface-card border border-theme-border rounded-xl">
            <span className="material-symbols-outlined text-2xl text-blue-400 mb-1">bolt</span>
            <span className="text-xl font-bold text-content-primary">{season_xp.toLocaleString('pt-BR')}</span>
            <span className="text-xs text-content-muted">{season_name ? `XP ${season_name}` : 'XP Temporada'}</span>
          </div>
          <div className="flex flex-col items-center p-4 bg-surface-card border border-theme-border rounded-xl">
            <span className="material-symbols-outlined text-2xl text-amber-400 mb-1">leaderboard</span>
            <span className="text-xl font-bold text-content-primary">{ranking_position ? `#${ranking_position}` : '—'}</span>
            <span className="text-xs text-content-muted">Ranking Geral</span>
          </div>
          <div className="flex flex-col items-center p-4 bg-surface-card border border-theme-border rounded-xl">
            <span className="material-symbols-outlined text-2xl text-orange-400 mb-1">local_fire_department</span>
            <span className="text-xl font-bold text-content-primary">{user.streak}</span>
            <span className="text-xs text-content-muted">Dias de Streak</span>
          </div>
        </section>

        {/* Badges */}
        <section className="p-6 bg-surface-card border border-theme-border rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-content-primary flex items-center gap-2">
              <span className="material-symbols-outlined text-accent">emoji_events</span>
              Conquistas
              {total_badges > 0 && (
                <span className="text-sm font-normal text-content-muted">({total_badges})</span>
              )}
            </h2>
          </div>
          {total_badges === 0 ? (
            <p className="text-sm text-content-muted text-center py-4">Nenhuma conquista desbloqueada ainda</p>
          ) : (
            <div className="flex flex-col gap-3">
              {rarest_badge && !recent_badges.some(b => b.id === rarest_badge.id) && (
                <div>
                  <p className="text-xs font-bold text-amber-400 uppercase tracking-wider mb-2">Mais Rara</p>
                  <BadgeCard badge={rarest_badge} highlight />
                </div>
              )}
              {recent_badges.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-content-muted uppercase tracking-wider mb-2">Recentes</p>
                  <div className="flex flex-col gap-2">
                    {recent_badges.map((badge) => (
                      <BadgeCard key={badge.id} badge={badge} highlight={rarest_badge?.id === badge.id} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Courses */}
        <section className="p-6 bg-surface-card border border-theme-border rounded-xl">
          <h2 className="text-lg font-bold text-content-primary flex items-center gap-2 mb-4">
            <span className="material-symbols-outlined text-accent">school</span>
            Cursos
          </h2>

          {/* In Progress */}
          {courses_in_progress.length > 0 && (
            <div className="mb-6">
              <p className="text-xs font-bold text-content-muted uppercase tracking-wider mb-3">Em Progresso</p>
              <div className="flex flex-col gap-3">
                {courses_in_progress.map((course) => (
                  <Link
                    key={course.id}
                    to={`/trilhas/${course.id}`}
                    className="flex items-center gap-4 p-3 rounded-lg border border-theme-border hover:border-accent/30 transition-all"
                  >
                    {course.thumbnail ? (
                      <img src={course.thumbnail} alt={course.title} className="size-10 rounded-lg object-cover shrink-0" />
                    ) : (
                      <div className="size-10 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-accent">route</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-content-primary truncate">{course.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 h-1.5 bg-surface-tertiary rounded-full overflow-hidden">
                          <div className="h-full bg-accent rounded-full" style={{ width: `${course.progress_percentage}%` }} />
                        </div>
                        <span className="text-xs text-content-muted shrink-0">{course.progress_percentage}%</span>
                      </div>
                    </div>
                    <span className="material-symbols-outlined text-content-muted">chevron_right</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Completed */}
          {courses_completed.length > 0 && (
            <div>
              <p className="text-xs font-bold text-content-muted uppercase tracking-wider mb-3">Concluidos</p>
              <div className="flex flex-col gap-3">
                {courses_completed.map((course) => (
                  <Link
                    key={course.id}
                    to={`/trilhas/${course.id}`}
                    className="flex items-center gap-4 p-3 rounded-lg border border-theme-border hover:border-green-500/30 transition-all"
                  >
                    {course.thumbnail ? (
                      <img src={course.thumbnail} alt={course.title} className="size-10 rounded-lg object-cover shrink-0" />
                    ) : (
                      <div className="size-10 rounded-lg bg-green-500/10 flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-green-500">check_circle</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-content-primary truncate">{course.title}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-green-400 font-medium">{course.xp_earned} XP</span>
                        {course.completed_at && (
                          <span className="text-xs text-content-muted">
                            {new Date(course.completed_at).toLocaleDateString('pt-BR')}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {courses_in_progress.length === 0 && courses_completed.length === 0 && (
            <p className="text-sm text-content-muted text-center py-4">Nenhum curso iniciado ainda</p>
          )}
        </section>

        {/* Certificates */}
        <section className="p-6 bg-surface-card border border-theme-border rounded-xl" data-testid="certificates-section">
          <h2 className="text-lg font-bold text-content-primary flex items-center gap-2 mb-4">
            <span className="material-symbols-outlined text-accent">workspace_premium</span>
            Meus Certificados
            {certificates.length > 0 && (
              <span className="text-sm font-normal text-content-muted">({certificates.length})</span>
            )}
          </h2>
          {certificates.length === 0 ? (
            <p className="text-sm text-content-muted text-center py-4">Complete um curso para receber seu certificado</p>
          ) : (
            <div className="flex flex-col gap-3">
              {certificates.map((cert) => (
                <div
                  key={cert.id}
                  className="flex items-center gap-4 p-3 rounded-lg border border-theme-border"
                >
                  <div className="size-10 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                    <span className="material-symbols-outlined text-amber-400">workspace_premium</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-content-primary truncate">{cert.trail_title}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-content-muted">{cert.hours}h</span>
                      <span className="text-xs text-content-muted">
                        {new Date(cert.issued_at).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => {
                        const url = `${window.location.origin}/verificar-certificado/${cert.verification_code}`;
                        navigator.clipboard.writeText(url).catch(() => {});
                      }}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-white/5 border border-theme-border text-content-secondary hover:text-content-primary transition-colors"
                      title="Copiar link de verificacao"
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>link</span>
                      Verificar
                    </button>
                    <Link
                      to={`/certificado/${cert.trail_id}`}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-accent/10 text-accent hover:bg-accent/20 transition-colors"
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>download</span>
                      Baixar
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Season History */}
        {season_history.length > 0 && (
          <section className="p-6 bg-surface-card border border-theme-border rounded-xl">
            <h2 className="text-lg font-bold text-content-primary flex items-center gap-2 mb-4">
              <span className="material-symbols-outlined text-accent">history</span>
              Historico de Temporadas
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-theme-border">
                    <th className="text-left py-2 px-3 text-content-muted font-medium">Temporada</th>
                    <th className="text-center py-2 px-3 text-content-muted font-medium">Posição</th>
                    <th className="text-right py-2 px-3 text-content-muted font-medium">XP</th>
                  </tr>
                </thead>
                <tbody>
                  {season_history.map((entry) => (
                    <tr key={entry.season_id} className="border-b border-theme-border/50 last:border-0">
                      <td className="py-2.5 px-3 font-medium text-content-primary">{entry.season_name}</td>
                      <td className="py-2.5 px-3 text-center">
                        {entry.final_position ? (
                          <span className={`font-bold ${entry.final_position <= 3 ? 'text-amber-400' : 'text-content-primary'}`}>
                            #{entry.final_position}
                          </span>
                        ) : (
                          <span className="text-content-muted">—</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-right text-content-secondary">{entry.total_xp.toLocaleString('pt-BR')} XP</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <ProfileEditModal
          user={{ name: user.name, bio: user.bio || '', avatar: user.avatar, sector: user.sector }}
          onClose={() => setShowEditModal(false)}
          onSave={async (updates) => {
            await updateProfile(updates);
            await refreshUser();
            await refetch();
            setShowEditModal(false);
          }}
        />
      )}

      {/* Toast de preferências */}
      {prefsToast && (
        <div
          className="fixed bottom-6 right-6 px-4 py-3 rounded-lg text-sm font-medium text-white shadow-lg bg-surface-secondary border border-theme-border z-50 max-w-sm"
          data-testid="profile-prefs-toast"
        >
          {prefsToast}
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
